import React, { useState } from "react";
import { createUseStyles } from "react-jss";
import { fetchPoliciesB2B } from "../../../../../api/CRM/PoliciesB2B/PoliciesB2B";
import Loader from "../../../../../components/Loader";
import {
  genericIndexesStyles,
  tableStyles,
  variables,
} from "../../../../../variables";
import Navbar from "./Navbar";
import ControlStrip from "../../../../../components/ControlStrip";
import _ from "lodash";
import { shortDater } from "../../../../../helpers";
import { medWeekdayDatetimer } from "../../../../../helpers";
import { DateTime } from "luxon";
import DetailB2BModal from "../B2BModals/DetailB2BModal";
import HamburgerMenu from "../../../../../components/HamburgerMenu";
import { useListPageSidebar } from "../../../../../hooks";
import * as yup from "yup";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import { genericRequestErrorHandler } from "../../../../../api";
import { CustomButton } from "../../../../../components/CustomButton";

export const policiesStatusMapping = {
  AC: "Active",
  IN: "Inactive",
  CA: "Candidate",
};

const initialFilters = {
  limit: 20,
  page: 1,
  status: "",
  name__icontains: "",
  booking_window_from__gte: "",
  booking_window_to__lte: "",
  travel_entities__contains: "",
  service_execution_to__lte: "",
  service_execution_from__gte: "",
  ordering: "",
  discending: "",
  created__gte: "",
  created__lte: "",
  source_entity: "",
  policy_type: "",
};

const navbarSchema = yup.object().shape({
  name__icontains: yup
    .string()
    .min(2, "Name must be more than 2 characters")
    .max(100, "Name: Must be less than 100 characters"),
  created__gte: yup.date(),
  created__lte: yup.date().max(new Date(), "Date cannot be in the future"),
});

const PolicieB2BStyles = createUseStyles({
  PolicieB2B: genericIndexesStyles.indexes.mobile(),
  tableContainer: genericIndexesStyles.tableContainer.mobile(),
  table: tableStyles.table,
  thead: tableStyles.head,
  headRow: tableStyles.head.row,
  headCell: tableStyles.head.cell,
  bodyRow: tableStyles.body.row,
  tableCell: tableStyles.body.cell,
  actionCell: genericIndexesStyles.actionCell.mobile,
  paxCell: { ...tableStyles.body.cell, width: "4rem", minWidth: "4rem" },
  distributionPeriodCell: { ...tableStyles.body.cell, width: "12rem" },
  reservationPeriodCell: { ...tableStyles.body.cell, width: "8rem" },
  roundingDigitsCell: { ...tableStyles.body.cell, width: "0.1rem" },
  datesCell: { ...tableStyles.body.cell, width: "13rem" },
  [`@media ${variables.media.smallscreen}`]: {
    actionCell: {
      padding: `calc(${variables.double_gap} * 2 ) 0!important`,
    },
    tableCell: {
      padding: `calc(${variables.normal_gap} * 2.5) 0`,
    },
  },
});
const PoliciesB2B = () => {
  const classes = PolicieB2BStyles();

  useListPageSidebar();

  const [filters, setFilters] = useState(initialFilters);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [B2BId, setB2BId] = useState(null);

  const tableHeaders = {
    "Policy Name": (val) => _.capitalize(_.get(val, "name")),
    "Source Entity": (val) =>
      `${val.source_entity_id}___${val.source_content_type}`,
    "Policy Type": (val) => val.policy_type,
    Pax: (val) =>
      `${val.pax_from ? val.pax_from : "N/A"} to ${
        val.pax_to ? val.pax_to : "N/A"
      }`,
    "Distribution Period": (val) => (
      <React.Fragment>
        <span>
          {val.booking_window_from
            ? shortDater(val.booking_window_from)
            : "N/A"}
        </span>
        <br />
        <span>-</span>
        <br />
        <span>
          {val.booking_window_to ? shortDater(val.booking_window_to) : "N/A"}
        </span>
      </React.Fragment>
    ),

    "Reservation Period": (val) => (
      <React.Fragment>
        <span>
          {val.service_execution_from
            ? shortDater(val.service_execution_from)
            : "N/A"}
        </span>
        <br />
        <span>-</span>
        <br />
        <span>
          {val.service_execution_to
            ? shortDater(val.service_execution_to)
            : "N/A"}
        </span>
      </React.Fragment>
    ),
    "Rounding Digits": (val) => val.rounding_digits || "N/A",
    Created: (val) => val.created || "N/A",
    Status: (val) =>
      _.get(policiesStatusMapping, _.get(val, "status")) || "N/A",
  };

  const handleApplyFilters = async (filters) => {
    if (filters.source_entity !== "") {
      try {
        const filteredFilters = Object.keys(filters).reduce((acc, key) => {
          if (filters[key] !== "") {
            acc[key] = filters[key];
          }
          return acc;
        }, {});

        await navbarSchema.validate(filteredFilters, { abortEarly: false });
        setFilters(filteredFilters);
      } catch (error) {
        const validationErrors = {};
        error.inner.forEach((err) => {
          if (!validationErrors[err.path]) {
            validationErrors[err.path] = err.message;
            toast.error(err.message);
          }
        });
      }
    } else {
      toast.error(`You must choose Source Entity`);
    }
  };

  const { data: policiesB2B, isLoading } = useQuery({
    queryKey: ["PoliciesB2B", filters],
    queryFn: () => {
      const memberId = _.get(filters, "source_entity", []).split("___")[0];
      let correctPayload = { ...filters, source_entity_id: memberId };
      delete correctPayload["source_entity"];
      if (filters.created__lte) {
        correctPayload["created__lte"] = DateTime.fromISO(
          filters.created__lte
        ).toISO();
      }
      if (filters.created__gte) {
        correctPayload["created__gte"] = DateTime.fromISO(
          filters.created__gte
        ).toISO();
      }
      return fetchPoliciesB2B({
        params: correctPayload,
      });
    },
    enabled: !showDetailModal,
    onError: (error) => genericRequestErrorHandler(error),
  });

  return (
    <div className={classes.PolicieB2B}>
      <Navbar
        filters={filters}
        initialFilters={initialFilters}
        onApplyFilters={handleApplyFilters}
      />
      <HamburgerMenu />
      <ControlStrip
        limit={filters.limit}
        page={filters.page}
        count={_.get(policiesB2B, "data.count", 0)}
        onChangeLimit={(limit) => setFilters({ ...filters, limit, page: 1 })}
        onChangePage={(page) => setFilters({ ...filters, page })}
        orderOptions={[["created", "Created"]]}
        onChangeOrderOptions={(ordering) =>
          setFilters({ ...filters, ordering })
        }
      />
      {isLoading && <Loader onTop={true} />}
      <div className={classes.tableContainer}>
        <table className={classes.table}>
          <thead className={classes.thead}>
            <tr className={classes.headRow}>
              <th className={classes.headCell}>#</th>
              {Object.keys(tableHeaders).map((value, idx) => (
                <th className={classes.headCell} key={idx}>
                  {value}
                </th>
              ))}
              <th className={classes.headCell}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {_.get(policiesB2B, "data.results", []).map((val, idx) => (
              <tr className={classes.bodyRow} key={idx}>
                <td className={classes.tableCell}>{idx + 1}</td>
                {Object.entries(tableHeaders).map(([key, fn], ridx) => {
                  var cell = null;
                  switch (key.toLowerCase()) {
                    case "last edited":
                      cell = fn(val) ? medWeekdayDatetimer(fn(val)) : "N/A";
                      return (
                        <td className={classes.datesCell} key={ridx}>
                          {cell}
                        </td>
                      );
                    case "created":
                      cell = fn(val) ? medWeekdayDatetimer(fn(val)) : "N/A";
                      return (
                        <td className={classes.datesCell} key={ridx}>
                          {cell}
                        </td>
                      );
                    case "rounding digits":
                      cell = fn(val) || "N/A";
                      return (
                        <td className={classes.roundingDigitsCell} key={ridx}>
                          {cell}
                        </td>
                      );
                    case "reservation period":
                      cell = fn(val) || "N/A";
                      return (
                        <td
                          className={classes.reservationPeriodCell}
                          key={ridx}>
                          {cell}
                        </td>
                      );
                    case "distribution period":
                      cell = fn(val) || "N/A";
                      return (
                        <td
                          className={classes.distributionPeriodCell}
                          key={ridx}>
                          {cell}
                        </td>
                      );
                    case "pax":
                      cell = fn(val) || "N/A";
                      return (
                        <td className={classes.paxCell} key={ridx}>
                          {cell}
                        </td>
                      );
                    default:
                      cell = fn(val) || "N/A";
                      return (
                        <td className={classes.tableCell} key={ridx}>
                          {cell}
                        </td>
                      );
                  }
                })}
                <td className={classes.actionCell}>
                  <CustomButton
                    appearance="ghost"
                    onClick={() => {
                      setB2BId(val.id);
                      setShowDetailModal(true);
                    }}>
                    <strong>Details</strong>
                  </CustomButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showDetailModal && (
        <DetailB2BModal
          b2bId={B2BId}
          onClose={() => {
            setShowDetailModal(false);
            setB2BId(null);
          }}
        />
      )}
    </div>
  );
};

export default PoliciesB2B;
